import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import PermissionService from 'components/Common/PermissionService';
import React, { useCallback, useMemo, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Box, Button } from '@mui/material';
import { FlowVersionHistoryContext } from './FlowVersionHistoryProvider';
import {
  HDialogTitle, HSpinner, HTablePagination,
} from 'BaseComponents';
import { OWNERSHIP_RESTRICTION_TOOLTIP } from 'pages/Flow/constants';
import { activeOrgSelector } from 'redux/reducer/AuthenticationReducer';
import { cleanRoom } from 'utils/spaUrls';
import { habuColors } from 'Theme';
import {
  useDeleteFlowVersion,
  useFetchFlowVersionHistory,
  useGetCleanRoomFlowRunsCount,
  useSetCleanroomFlowVersion,
} from 'api/flows';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { userPermissions } from 'utils/appConstants';


const FlowVersionHistoryDialog = ({ onVersionUpdate }) => {
  const history = useHistory();
  const { crId } = useParams();
  const { ID: orgId } = useSelector(activeOrgSelector);
  const {
    isOpen, closeModal, modalDetails,
    handleDeleteFlowVersionDialogOpen,
  } = React.useContext(FlowVersionHistoryContext);
  const [filters, setFilters] = useState({ offset: 0, limit: 15 });
  const [versionToSet, setVersionToSet] = useState();
  const [versionToDelete, setVersionToDelete] = useState(-1);
  const {
    data, isSuccess, isLoading,
  } = useFetchFlowVersionHistory({
    flowId: modalDetails?.flowID,
    offset: filters.offset,
    limit: filters.limit,
    enabled: isOpen,
  });
  const {
    data: flowVersionRunsCount,
    isSuccess: flowVersionRunsCountSuccess,
  } = useGetCleanRoomFlowRunsCount({
    crFlowId: modalDetails?.ID,
    crId,
    version: versionToDelete,
    enabled: isOpen && versionToDelete !== -1,
  });
  const updateActiveFlowVersion = useSetCleanroomFlowVersion();
  const deleteFlowVersion = useDeleteFlowVersion(crId, modalDetails?.flowID);
  const canEditFlows = PermissionService.isAuthorized([userPermissions.crFlowsBuilder]);

  const isFlowOwnedByCurrentUserOrg = useMemo(() => (
    modalDetails?.flow?.organizationID === orgId
  ), [modalDetails, orgId]);

  const currentVersion = useMemo(() => modalDetails?.flowVersion ?? null, [modalDetails]);
  const isCurrentVersion = useCallback((version) => (version === currentVersion), [currentVersion]);

  const handleModalClose = () => {
    setVersionToSet();
    setVersionToDelete(-1);
    closeModal();
  };

  const onVersionPreview = (version) => {
    history.push(
      cleanRoom.flowViewWithVersion(crId, modalDetails.ID, modalDetails.flowID, version),
    );
  };
  const onVersionSelect = (version) => {
    setVersionToSet(version);
    updateActiveFlowVersion.mutate({
      cleanRoomId: crId,
      crFlowId: modalDetails?.ID,
      version,
    });
  };
  const onVersionDelete = () => {
    // Trigger dialog if the flow at version to delete has flow runs
    if (flowVersionRunsCount !== undefined && flowVersionRunsCount > 0) {
      handleDeleteFlowVersionDialogOpen({
        runCount: flowVersionRunsCount,
        crFlow: modalDetails,
        version: versionToDelete,
      });

      handleModalClose();
      return;
    }

    // If not runs made, trigger the flow version deletion
    deleteFlowVersion.mutate({
      version: versionToDelete,
      cleanRoomID: crId,
    });

    handleModalClose();
  };

  const handlePaginationChange = (newFilters) => {
    setFilters(newFilters);
  };

  // On update flow version success, close modal
  React.useEffect(() => {
    if (updateActiveFlowVersion.isSuccess) {
      onVersionUpdate();
      handleModalClose();
      updateActiveFlowVersion.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateActiveFlowVersion]);

  // On versionToDelete change, fetch flow runs and check if confirmation is needed
  React.useEffect(() => {
    if (versionToDelete !== -1 && flowVersionRunsCountSuccess) {
      onVersionDelete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowVersionRunsCountSuccess]);

  const count = data?.data.count || 0;
  return (
    <Dialog onClose={handleModalClose} open={isOpen} fullWidth maxWidth='lg'>
      <HDialogTitle onClose={handleModalClose}>
        <Typography variant='h3'>Flow Version History</Typography>
      </HDialogTitle>
      <DialogContent dividers>
        {isLoading && <HSpinner />}
        {isSuccess && (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Flow Version</TableCell>
                    <TableCell>
                      <Typography variant='subtitle2' sx={{ color: habuColors.neutral['60'] }}>
                        Change Message
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='subtitle2' sx={{ color: habuColors.neutral['60'] }}>
                        Actions
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.data.versions.map((version) => (
                    <TableRow>
                      <TableCell>
                        {`v${version.version}${isCurrentVersion(version.version) ? ' (Current version)' : ''}`}
                      </TableCell>
                      <TableCell>
                        <Typography variant='subtitle2' sx={{ color: habuColors.neutral['60'] }}>
                          {version.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display='flex' columnGap={1}>
                          <Button
                            variant='contained'
                            data-testid='preview-version-button'
                            {...(isCurrentVersion(version.version) ? {
                              disabled: true,
                            } : {
                              onClick: () => onVersionPreview(version.version),
                            })}
                          >
                            Preview this version
                          </Button>
                          <Button
                            variant='outlined'
                            data-testid='select-version-button'
                            endIcon={(
                              updateActiveFlowVersion.isLoading
                              && versionToSet === version.version
                            ) ? <HSpinner isButton />
                              : null}
                            {...((
                              isCurrentVersion(version.version)
                            ) ? {
                                disabled: true,
                              } : {
                                disabled: updateActiveFlowVersion.isLoading,
                                onClick: () => onVersionSelect(version.version),
                              })}
                          >
                            Use this version
                          </Button>
                          {canEditFlows && (
                            <Tooltip title={!isFlowOwnedByCurrentUserOrg ? OWNERSHIP_RESTRICTION_TOOLTIP : ''}>
                              <Button
                                variant='outlined'
                                data-testid='delete-version-button'
                                endIcon={(
                                  deleteFlowVersion.isLoading
                                  && versionToDelete === version.version
                                ) ? <HSpinner isButton />
                                  : null}
                                {...((
                                  isCurrentVersion(version.version) || !isFlowOwnedByCurrentUserOrg
                                ) ? {
                                    disabled: true,
                                  } : {
                                    disabled: deleteFlowVersion.isLoading,
                                    onClick: () => setVersionToDelete(version.version),
                                  })}
                              >
                                Delete
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <HTablePagination
              count={count}
              filters={filters}
              onChange={handlePaginationChange}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FlowVersionHistoryDialog;
